/**
 * update-controller — explicit, throttled service-worker update checks.
 *
 * The iOS-standalone-critical piece of the update-delivery fix
 * (fix-pwa-update-delivery, 2026-07-17). An installed PWA on iOS is relaunched
 * as a fresh webview but is NOT a fresh navigation: the browser does not
 * re-fetch sw.js on its own at the moments a user would expect (cold launch,
 * returning to the app). Without an explicit `registration.update()` nudge at
 * those moments, a new deployment is never detected and the app stays pinned to
 * the old bundle across close/reopen cycles.
 *
 * This module drives `registration.update()`:
 *   - once on start (forced, after `navigator.serviceWorker.ready`), and
 *   - on every visibilitychange → visible and window `focus` (the iOS
 *     standalone "resume" signals), throttled to at most one check per
 *     UPDATE_CHECK_THROTTLE_MS so a rapid focus/blur flurry cannot hammer the
 *     network.
 *
 * Gating on `navigator.serviceWorker.ready` is deliberate: calling update()
 * before the registration's active worker is settled is a no-op on iOS. Waiting
 * for `ready` guarantees there is a registration to update.
 *
 * Detection of the resulting waiting worker is handled by AutoUpdater
 * (useRegisterSW's needRefresh) + version-probe (commit drift). This module
 * only triggers the check; it renders nothing and owns no UI.
 */

/** At most one update() network check per this interval (except forced). */
export const UPDATE_CHECK_THROTTLE_MS = 60_000;

let lastCheckAt = 0;
let started = false;

/**
 * Run one throttled service-worker update check.
 *
 * @param force — bypass the throttle (used for the initial start-up check).
 *
 * Awaits `navigator.serviceWorker.ready` before calling `registration.update()`
 * so the check is a no-op-free real re-fetch of sw.js. Failures (offline, no
 * registration) are console.warn only — a background update nudge failing is
 * not user-actionable, and the next resume retries. This is an observational
 * helper, not a user-action handler (Rule 8 does not require a visible error).
 */
export async function checkForUpdate(force = false): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return;

  const now = Date.now();
  if (!force && now - lastCheckAt < UPDATE_CHECK_THROTTLE_MS) return;
  lastCheckAt = now;

  try {
    // `ready` resolves once the registration has an active worker. On a repeat
    // launch it resolves immediately; on a first-ever load it waits for the
    // initial install to activate. Either way we then hold a live registration.
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
  } catch (err) {
    console.warn("[proof update-controller] service-worker update check failed:", err);
  }
}

/**
 * Start the update-check listeners.
 *
 * Call once from entry.client.tsx. Idempotent — a second call is a no-op.
 * No-ops when navigator/document/window are unavailable (SSR-safe).
 *
 * Wires:
 *   - an immediate forced check on start (past the throttle),
 *   - a throttled check on visibilitychange → visible,
 *   - a throttled check on window `focus` (iOS standalone resume often fires
 *     `focus` without a visibilitychange).
 */
export function startUpdateChecks(): void {
  if (typeof navigator === "undefined" || typeof document === "undefined") return;
  if (started) return;
  started = true;

  // Initial forced check — bypasses the throttle so a launch always checks.
  void checkForUpdate(true);

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void checkForUpdate();
  });

  if (typeof window !== "undefined") {
    window.addEventListener("focus", () => {
      void checkForUpdate();
    });
  }
}

/**
 * Reset module state.
 *
 * @internal — test-only. Lets tests re-exercise the throttle + start guard
 *   without vi.resetModules(). Never call from application code.
 */
export function resetUpdateControllerForTesting(): void {
  lastCheckAt = 0;
  started = false;
}
